import { TicketingAcquisitionMethod } from '../../graphql';
import { TicketingConnectionRow, TicketingConnectionStatus } from '../../utils';

/**
 * How sure we are that this disconnect reaches GitHub.
 *
 * `certain` is a connection GitHub has confirmed installed, suspended or not. `possible` is one
 * still waiting on its install, which can hold a real installation: the install claim writes the
 * installation id and only promotes the row once the signed webhook lands, so a late or lost
 * webhook leaves an installed app on a waiting row — and the backend uninstalls on the id, not the
 * status. Promising nothing there is the surprise the confirmation exists to prevent, so the copy
 * hedges instead.
 */
export const gitHubUninstallCertainty = (row: TicketingConnectionRow): 'certain' | 'possible' | 'none' => {
  if (row.token.acquisitionMethod !== TicketingAcquisitionMethod.GithubApp) {
    return 'none';
  }

  switch (row.status) {
    case TicketingConnectionStatus.Connected:
    case TicketingConnectionStatus.SuspendedInGitHub:
      return 'certain';
    case TicketingConnectionStatus.WaitingForInstall:
      return 'possible';
    // Already uninstalled from GitHub — there is nothing left to remove there.
    default:
      return 'none';
  }
};

const uninstallSentence = (row: TicketingConnectionRow): string => {
  const organization = row.organization ?? 'GitHub';

  switch (gitHubUninstallCertainty(row)) {
    case 'certain':
      return (
        ` The Neo.Tax app will be uninstalled from your ${organization} organization, so reconnecting later needs a ` +
        'GitHub org admin.'
      );
    case 'possible':
      return (
        ` If the install did complete, the Neo.Tax app will be uninstalled from your ${organization} organization, so ` +
        'reconnecting later needs a GitHub org admin.'
      );
    default:
      return '';
  }
};

/**
 * What the confirmation says will happen. Assembled here rather than in the component so the copy
 * for each connection state is readable in one place, and so it can be tested without rendering.
 *
 * Four things the customer has to know before committing: what is kept, what actually goes, whether
 * anything already queued is being cancelled, and — for a connection with a live GitHub
 * installation — that disconnecting reaches into their GitHub organization, which nothing else on
 * this page does.
 */
export const disconnectConfirmationCopy = (row: TicketingConnectionRow): string => {
  const kept =
    'Everything already synced through this connection is kept — tickets, contributors, workstreams, and the ' +
    'record of every past sync.';

  // The credential going away is the whole point, but it is also the part that decides how much work
  // reconnecting is, and for a Merge-managed connection that is more than it looks: the linked
  // account at Merge goes too, so reconnecting is a fresh authorization rather than a re-entry.
  const removed =
    row.token.acquisitionMethod === TicketingAcquisitionMethod.MergeDev
      ? ` The credential is removed, along with the linked account at Merge, so reconnecting ${row.provider} means ` +
        'authorizing it again from scratch.'
      : " The credential and this connection's own settings are removed.";

  // Null is "we could not count them", which is not the same as none: the customer still needs to
  // know a disconnect cancels what is queued, so that case hedges rather than claiming a number or
  // staying silent.
  const pendingSyncs = row.token.pendingScheduledSyncCount;
  const scheduled =
    pendingSyncs === null || pendingSyncs === undefined
      ? ' Any scheduled syncs that have not run yet will be cancelled.'
      : pendingSyncs > 0
        ? ` ${pendingSyncs} scheduled sync${pendingSyncs === 1 ? '' : 's'} will be cancelled.`
        : '';

  return `${kept}${removed}${scheduled}${uninstallSentence(row)}`;
};
